import PropTypes from 'prop-types'
import React, {useState, useRef} from 'react'
import {FormattedMessage} from 'react-intl'
import {useOutsideClick} from '@chakra-ui/react'
import {
    Box,
    VStack,
    Checkbox,
    Divider,
    Button
} from '@salesforce/retail-react-app/app/components/shared/ui'
import CreateNewListModal from '../create-new-list-modal'
import {HeartIconBlack, HeartIconRed} from '../../components/icons'
import {useCurrentCustomer} from '../../hooks/use-current-customer'
import {useShopperCustomersMutation} from '@salesforce/commerce-sdk-react'
import {useToast} from '@salesforce/retail-react-app/app/hooks/use-toast'
import {useGtm} from '../../integrations/gtm/app/hooks/use-gtm'
import {useTrackThinkAnalyticsEvents} from '../../hooks/use-track-thinkanalytics-events'
import {
    API_ERROR_MESSAGE,
    TOAST_MESSAGE_ADDED_TO_GUEST_WISHLIST,
    TOAST_MESSAGE_REMOVED_FROM_WISHLIST
} from '../../constants'
import useSegment from '../../integrations/segment/app/hooks/use-segment'
import {useIntl} from 'react-intl'
import {useWishList} from '../../hooks/use-wish-list'

const WishlistDropdownButton = ({
    product,
    isWishlistLoading,
    canAddToWishlist,
    ariaLabel,
    styles,
    isMobile,
    isFavourite,
    openFromEdit = false,
    currentListId = null
}) => {
    const {data: customer} = useCurrentCustomer()
    const customerId = customer?.customerId
    const {formatMessage} = useIntl()
    const {data: wishlistData} = useWishList()
    const [isOpen, setIsOpen] = useState(openFromEdit)
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
    const ref = useRef()
    const {mutate: trackTAEvent} = useTrackThinkAnalyticsEvents()
    const toast = useToast()
    const gtm = useGtm()
    const segment = useSegment()
    const {mutateAsync: createCustomerProductListItem} = useShopperCustomersMutation(
        'createCustomerProductListItem'
    )
    const {mutateAsync: deleteCustomerProductListItem} = useShopperCustomersMutation(
        'deleteCustomerProductListItem'
    )

    useOutsideClick({ref, handler: () => setIsOpen(false)})

    const addItemToWishlist = async (event, product, listId, list, customerId) => {
        try {
            await createCustomerProductListItem({
                parameters: {customerId, listId},
                body: {
                    quantity: 1,
                    public: false,
                    priority: 1,
                    type: 'product',
                    productId: product?.productId || product?.id
                }
            })
            toast({
                title: formatMessage(TOAST_MESSAGE_ADDED_TO_GUEST_WISHLIST),
                status: 'success',
                duration: 3000
            })
            trackTAEvent({
                c_productId: product?.productId || product?.id,
                c_useCase: 'Store%2FLearn%2FClickedOnSearch'
            })
            gtm.sendProductToWishList('addToWishlist', product)
            segment.sendProductToWishList(product, list, product, event)
        } catch {
            toast({title: formatMessage(API_ERROR_MESSAGE), status: 'error', duration: 3000})
        }
    }

    const removeItemFromWishlist = async (event, product, listId, list, customerId) => {
        try {
            const itemId = list?.customerProductListItems.find(
                (item) => item?.productId === (product?.productId || product?.id)
            )?.id
            await deleteCustomerProductListItem({
                body: {},
                parameters: {customerId, listId, itemId}
            })
            toast({title: formatMessage(TOAST_MESSAGE_REMOVED_FROM_WISHLIST), status: 'error'})
            segment.sendRemoveProductFromWishlist(product, list, event)
        } catch {
            toast({title: formatMessage(API_ERROR_MESSAGE), status: 'error'})
        }
    }

    const isProductAvailableInList = (list) =>
        list?.customerProductListItems?.some((item) => item?.productId === product?.id)

    const handleToggle = () => setIsOpen((prev) => !prev)

    const handleWishlistItem = (event, list) => {
        const isInList = isProductAvailableInList(list)
        if (isInList) {
            removeItemFromWishlist(event, product, list?.id, list, customerId)
        } else {
            addItemToWishlist(event, product, list?.id, list, customerId)
        }
        setIsOpen(false)
    }

    return (
        <>
            <Box position="relative" ref={ref}>
                {!openFromEdit && (
                    <Button
                        onClick={() =>
                            !openFromEdit ? setIsOpen((prev) => !prev) : handleToggle()
                        }
                        isLoading={isWishlistLoading}
                        disabled={!canAddToWishlist}
                        variant="outline"
                        aria-label={ariaLabel}
                        className="wishlist-button"
                        {...styles}
                    >
                        {isMobile ? ariaLabel : isFavourite ? <HeartIconRed /> : <HeartIconBlack />}
                    </Button>
                )}
                {isOpen && (
                    <Box
                        position="absolute"
                        top="100%"
                        right={0}
                        mt={2}
                        p={4}
                        minW="250px"
                        bg="white"
                        border="1px solid #ccc"
                        boxShadow="md"
                        borderRadius="md"
                        zIndex={99}
                    >
                        <Button
                            variant="link"
                            colorScheme="orange"
                            size="sm"
                            onClick={() => {
                                setIsCreateModalOpen(true)
                                setIsOpen(false)
                            }}
                            mb={3}
                        >
                            <FormattedMessage
                                defaultMessage="Create New List"
                                id="wishlist.modal_title"
                            />
                        </Button>

                        {wishlistData?.registeredUserWishlists?.length > 1 && <Divider mb={3} />}

                        <VStack align="start" spacing={2}>
                            {wishlistData?.registeredUserWishlists
                                ?.filter((list) => {
                                    if (!list?.name) return false
                                    if (openFromEdit && currentListId) {
                                        return list?.id !== currentListId
                                    }
                                    return true
                                })
                                .map((list) => (
                                    <Checkbox
                                        key={list?.id}
                                        isChecked={isProductAvailableInList(list)}
                                        onChange={(event) => handleWishlistItem(event, list)}
                                    >
                                        {list?.name}
                                    </Checkbox>
                                ))}
                        </VStack>
                    </Box>
                )}
            </Box>
            {isCreateModalOpen && (
                <CreateNewListModal
                    isOpen={isCreateModalOpen}
                    onClose={() => setIsCreateModalOpen(false)}
                    wishLists={wishlistData?.registeredUserWishlists}
                />
            )}
        </>
    )
}

WishlistDropdownButton.propTypes = {
    product: PropTypes.object.isRequired,
    variant: PropTypes.object,
    quantity: PropTypes.number,
    isWishlistLoading: PropTypes.bool,
    canAddToWishlist: PropTypes.bool,
    onAdd: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
    ariaLabel: PropTypes.string,
    styles: PropTypes.object,
    isMobile: PropTypes.bool,
    isFavourite: PropTypes.bool,
    openFromEdit: PropTypes.bool,
    currentListId: PropTypes.string
}

export default WishlistDropdownButton
